import React, { useState } from 'react'; // Re-added useState for info modal
import { useAppContext } from '../../context/AppContext';
import { X, Info } from 'lucide-react'; // Add Info icon

const SettingsModal = () => {
  const {
    closeSettingsModal,
    language, // Get language state
    handleLanguageChange, // Get language handler
    contentLanguage, // NEW: Get content language state
    handleContentLanguageChange, // NEW: Get content language handler
    ignoreAdultContent, // Get ignore adult content state
    handleIgnoreAdultContentChange, // Get ignore adult content handler
    favouriteSports, // Get favourite sports state
    handleFavouriteSportsChange, // Get favourite sports handler
    isDebugMode, // Get debug mode state
    handleDebugModeChange, // Get debug mode handler
    enablePerformanceTracking, // Get performance tracking state
    handlePerformanceTrackingChange, // Get performance tracking handler
    userTimezoneId, // Get timezone ID state
    availableTimezones, // Get available timezones
    handleTimezoneChange, // Get timezone change handler
    t, // Get translation function
  } = useAppContext();

  // Local state for the performance tracking info modal
  const [isTrackingInfoOpen, setIsTrackingInfoOpen] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md relative">
        <button
          onClick={closeSettingsModal}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
          aria-label="Close settings"
        >
          <X size={24} />
        </button>
        <h2 className="text-xl font-semibold mb-6 text-gray-800">{t('settings.title')}</h2>

        {/* Interface Language Selection Section */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
            {t('settings.language')}
          </label>
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="language"
                value="en"
                checked={language === 'en'}
                onChange={() => handleLanguageChange('en')}
              />
              <span className="ml-2 text-sm text-gray-700">{t('settings.language.english')}</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="language"
                value="fr"
                checked={language === 'fr'}
                onChange={() => handleLanguageChange('fr')}
              />
              <span className="ml-2 text-sm text-gray-700">{t('settings.language.french')}</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="language"
                value="nl"
                checked={language === 'nl'}
                onChange={() => handleLanguageChange('nl')}
              />
              <span className="ml-2 text-sm text-gray-700">{t('settings.language.dutch')}</span>
            </label>
          </div>
        </div>

        {/* Content Language Preference Section */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
            {t('settings.contentLanguage')}
          </label>
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="contentLanguage"
                value="all"
                checked={contentLanguage === 'all'}
                onChange={() => handleContentLanguageChange('all')}
              />
              <span className="ml-2 text-sm text-gray-700">{t('settings.language.all')}</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="contentLanguage"
                value="en"
                checked={contentLanguage === 'en'}
                onChange={() => handleContentLanguageChange('en')}
              />
              <span className="ml-2 text-sm text-gray-700">{t('settings.language.english')}</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="contentLanguage"
                value="fr"
                checked={contentLanguage === 'fr'}
                onChange={() => handleContentLanguageChange('fr')}
              />
              <span className="ml-2 text-sm text-gray-700">{t('settings.language.french')}</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="contentLanguage"
                value="nl"
                checked={contentLanguage === 'nl'}
                onChange={() => handleContentLanguageChange('nl')}
              />
              <span className="ml-2 text-sm text-gray-700">{t('settings.language.dutch')}</span>
            </label>
          </div>
        </div>

        {/* Ignore Adult Content Section */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
            {t('settings.ignoreAdultContent')}
          </label>
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="ignoreAdultContent"
                value="yes"
                checked={ignoreAdultContent === true}
                onChange={() => handleIgnoreAdultContentChange(true)}
              />
              <span className="ml-2 text-sm text-gray-700">{t('settings.yes')}</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="ignoreAdultContent"
                value="no"
                checked={ignoreAdultContent === false}
                onChange={() => handleIgnoreAdultContentChange(false)}
              />
              <span className="ml-2 text-sm text-gray-700">{t('settings.no')}</span>
            </label>
          </div>
        </div>

        {/* Favourite Sports Section */}
        <div className="mb-4">
          <label htmlFor="favouriteSports" className="block text-sm font-medium text-gray-700 mb-1 text-left">
            {t('settings.favouriteSports')}
          </label>
          <input
            type="text"
            id="favouriteSports"
            value={favouriteSports}
            onChange={(e) => handleFavouriteSportsChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder={t('settings.favouriteSportsPlaceholder')}
          />
        </div>

        {/* Debug Mode Section */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
            {t('settings.debugMode')}
          </label>
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="debugMode"
                value="true"
                checked={isDebugMode === true}
                onChange={() => handleDebugModeChange(true)}
              />
              <span className="ml-2 text-sm text-gray-700">{t('settings.true')}</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="debugMode"
                value="false"
                checked={isDebugMode === false}
                onChange={() => handleDebugModeChange(false)}
              />
              <span className="ml-2 text-sm text-gray-700">{t('settings.false')}</span>
            </label>
          </div>
        </div>

        {/* Performance Tracking Section */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left flex items-center">
            <span>{t('settings.enablePerformanceTracking')}</span>
            <button
              type="button"
              onClick={() => setIsTrackingInfoOpen(true)}
              aria-label="About performance tracking"
              className="ml-2 text-gray-400 hover:text-gray-600"
              title="What is performance tracking?"
            >
              <Info size={16} />
            </button>
          </label>
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="performanceTracking"
                value="true"
                checked={enablePerformanceTracking === true}
                onChange={() => handlePerformanceTrackingChange(true)}
              />
              <span className="ml-2 text-sm text-gray-700">{t('settings.on')}</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-indigo-600"
                name="performanceTracking"
                value="false"
                checked={enablePerformanceTracking === false}
                onChange={() => handlePerformanceTrackingChange(false)}
              />
              <span className="ml-2 text-sm text-gray-700">{t('settings.off')}</span>
            </label>
          </div>
        </div>

        {/* Timezone Selection Section */}
        <div className="mb-4">
          <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-1 text-left">
            {t('settings.timezone')}
          </label>
          <select
            id="timezone"
            value={userTimezoneId}
            onChange={(e) => handleTimezoneChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            {availableTimezones.map((tz) => (
              <option key={tz.id} value={tz.id}>
                {tz.name} ({tz.offset})
              </option>
            ))}
          </select>
        </div>

      {isTrackingInfoOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-[60]">
          <div className="bg-white p-5 rounded-lg shadow-xl w-full max-w-md relative">
            <button
              onClick={() => setIsTrackingInfoOpen(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
              aria-label="Close information"
            >
              <X size={20} />
            </button>
            <h3 className="text-lg font-semibold mb-3 text-gray-800">{t('settings.performanceTrackingInfoTitle')}</h3>
            <p className="text-sm text-gray-700">
              {t('settings.performanceTrackingInfoText')}
            </p>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default SettingsModal;
